import { handleApiResponse } from '@src/api/apiClient/axiosClient';
import { Chat, chatApiClient, LinkPreviewResponse } from '@src/api/shared/community/chatApiClient';
import logger from '@src/utils/logger';
import { useCallback, useState } from 'react';

interface HideLinkPreviewParams {
  channelCode: string;
  mutateChats: () => void;
  setLinkPreviewCache: React.Dispatch<
    React.SetStateAction<Record<string, LinkPreviewResponse | null>>
  >;
  allChats: Chat[];
}

export const useHideLinkPreview = ({
  channelCode,
  mutateChats,
  setLinkPreviewCache,
  allChats,
}: HideLinkPreviewParams) => {
  const [isHidePreviewModalOpen, setIsHidePreviewModalOpen] = useState(false);
  const [hidingPreviewChatId, setHidingPreviewChatId] = useState<number | null>(null);

  const performToggleLinkPreview = useCallback(
    async (chatId: number, hidePreview: boolean) => {
      const result = await chatApiClient.toggleLinkPreview(channelCode, chatId, hidePreview);
      handleApiResponse(
        result,
        () => {
          mutateChats();
        },
        (error) => {
          logger.error('Error toggling link preview:', error);
        },
      );
    },
    [channelCode, mutateChats],
  );

  const handleToggleLinkPreview = useCallback(
    (chatId: number, hidePreview: boolean) => {
      if (hidePreview) {
        // Show confirmation modal when hiding preview
        setHidingPreviewChatId(chatId);
        setIsHidePreviewModalOpen(true);
      } else {
        // Directly restore preview without confirmation
        performToggleLinkPreview(chatId, hidePreview);
      }
    },
    [performToggleLinkPreview],
  );

  const handleConfirmHidePreview = async () => {
    if (hidingPreviewChatId === null) {
      return;
    }

    setLinkPreviewCache((prev) => {
      const key = String(hidingPreviewChatId);
      return { ...prev, [key]: null };
    });

    setIsHidePreviewModalOpen(false);
    const chatIdToHide = hidingPreviewChatId;
    setHidingPreviewChatId(null);

    const result = await chatApiClient.toggleLinkPreview(channelCode, chatIdToHide, true);
    handleApiResponse(
      result,
      () => {
        mutateChats();
      },
      (error) => {
        logger.error('リンクプレビューの切り替え中にエラーが発生しました:', error);
        const chat = allChats.find((c) => c.id === chatIdToHide);
        if (chat && chat.preview) {
          setLinkPreviewCache((prev) => ({
            ...prev,
            [String(chatIdToHide)]: chat.preview ?? null,
          }));
        }
      },
    );
  };

  const handleCancelHidePreview = () => {
    setIsHidePreviewModalOpen(false);
    setHidingPreviewChatId(null);
  };

  return {
    isHidePreviewModalOpen,
    hidingPreviewChatId,
    handleToggleLinkPreview,
    handleConfirmHidePreview,
    handleCancelHidePreview,
  };
};
